import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import QuillResize from "quill-resize-module";
import "quill-resize-module/dist/resize.css";

// Registra una sola volta il modulo per il resize delle immagini
if (!Quill.imports["modules/resize"]) {
  Quill.register("modules/resize", QuillResize);
}

// Estendi il blot Image per permettere width, height e style (inline)
const BaseImage = Quill.import("formats/image");
if (!BaseImage.__attrsExtended) {
  BaseImage.__attrsExtended = true;
  const ATTRS = ["alt", "height", "width", "style", "class"];
  BaseImage.formats = function (domNode) {
    return ATTRS.reduce((acc, k) => {
      if (domNode.hasAttribute(k)) acc[k] = domNode.getAttribute(k);
      return acc;
    }, {});
  };
  BaseImage.prototype.format = function (name, value) {
    if (ATTRS.includes(name)) {
      if (value) this.domNode.setAttribute(name, value);
      else this.domNode.removeAttribute(name);
    } else {
      BaseImage.prototype.__proto__.format.call(this, name, value);
    }
  };
  Quill.register(BaseImage, true);
}

/**
 * Editor rich-text basato su Quill, simile a Word.
 * Bottoni: bold/italic/underline/strike, colori, liste, allineamento, link, immagini.
 * Espone `insertText(text)` via ref per inserire un placeholder alla posizione del cursore.
 */
const RichEditor = forwardRef(function RichEditor(
  { value, onChange, placeholder = "", height = 280, testid },
  ref
) {
  const quillRef = useRef(null);

  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      const q = quillRef.current?.getEditor?.();
      if (!q) return;
      const sel = q.getSelection(true) || { index: Math.max(0, q.getLength() - 1), length: 0 };
      if (sel.length > 0) q.deleteText(sel.index, sel.length, "user");
      q.insertText(sel.index, text, "user");
      q.setSelection(sel.index + text.length, 0, "user");
      q.focus();
    },
    focus: () => quillRef.current?.getEditor?.()?.focus(),
  }));

  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }, { indent: "-1" }, { indent: "+1" }],
      ["link", "image"],
      ["clean"],
    ],
    resize: {
      locale: {
        center: "centro",
      },
      // Mostra maniglie ai 4 angoli per ridimensionare proporzionalmente
      // Mostra anche barra di allineamento sotto l'immagine
    },
  }), []);

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "color", "background", "list", "bullet", "align", "indent",
    "link", "image", "width", "height", "style",
  ];

  return (
    <div data-testid={testid} className="rich-editor-wrap" style={{ background: "white", border: "1px solid #ccc", borderRadius: 3 }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{ minHeight: height }}
      />
    </div>
  );
});

export default RichEditor;
